// ═══════════════════════════════════════════════════════════════════════════════
// MICROSOFT GRAPH — MAIL SEND
// ═══════════════════════════════════════════════════════════════════════════════
// Sends email from the signed-in user's mailbox via /me/sendMail.
// Requires Mail.Send (delegated) — admin-consented in the PPUSA tenant.
// The message shows up in the sender's Sent Items so there's a native paper
// trail alongside whatever we log in outreach_log.

import { graphMailRequest } from "../auth/msalConfig";

const GRAPH_SEND_MAIL = "https://graph.microsoft.com/v1.0/me/sendMail";

// All LOIs are sent from this shared mailbox. The logged-in user's Graph
// token + "Send As" permission on this mailbox in Exchange Admin is what
// authorises it — the `from` field on the message tells Graph which
// mailbox to send from.
const SEND_FROM_ADDRESS = "acquisitions@proximityparks.com";
const SEND_FROM_NAME = "ProximityParks Acquisitions";

/**
 * Acquire a Graph access token silently, falling back to an interactive popup
 * if the silent flow requires user action (consent, fresh sign-in, etc.).
 */
async function getGraphToken(msalInstance) {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error("No signed-in account. Please sign in again.");
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ ...graphMailRequest, account });
    return result.accessToken;
  } catch (silentError) {
    // InteractionRequiredAuthError — fall back to a full-page redirect.
    // This will navigate away and come back; the user's send action will need
    // to be retried after sign-in resumes.
    await msalInstance.acquireTokenRedirect({ ...graphMailRequest, account });
    throw new Error("Re-authentication required. Please retry after the page reloads.");
  }
}

/**
 * Send an email via Microsoft Graph. If `html` is provided, it's sent as
 * HTML (with `body` reserved as the plain-text fallback that Outlook stores
 * in the saved Sent Items copy). Otherwise sends as plain text.
 *
 * @param {PublicClientApplication} msalInstance — from useMsal().instance
 * @param {object} opts
 * @param {string} opts.to — recipient email (required)
 * @param {string[]} [opts.cc] — CC recipients
 * @param {string[]} [opts.bcc] — BCC recipients
 * @param {string} opts.subject
 * @param {string} [opts.body] — plain-text body (used when no html provided)
 * @param {string} [opts.html] — HTML body; takes precedence if provided
 * @param {boolean} [opts.saveToSentItems=true]
 * @returns {Promise<void>} resolves on HTTP 202 from Graph
 */
export async function sendMail(msalInstance, opts) {
  const { to, cc = [], bcc = [], subject, body, html, saveToSentItems = true } = opts;

  if (!to) throw new Error("Missing recipient (to).");
  if (!subject) throw new Error("Missing subject.");
  if (!body && !html) throw new Error("Missing body or html.");

  const token = await getGraphToken(msalInstance);

  const message = {
    subject,
    body: html
      ? { contentType: "HTML", content: html }
      : { contentType: "Text", content: body },
    from: {
      emailAddress: { address: SEND_FROM_ADDRESS, name: SEND_FROM_NAME },
    },
    toRecipients: [{ emailAddress: { address: to } }],
    ccRecipients: cc.map((addr) => ({ emailAddress: { address: addr } })),
    bccRecipients: bcc.map((addr) => ({ emailAddress: { address: addr } })),
  };

  const res = await fetch(GRAPH_SEND_MAIL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message, saveToSentItems }),
  });

  // sendMail returns 202 Accepted with an empty body on success.
  if (!res.ok) {
    let detail = "";
    try {
      const json = await res.json();
      detail = json?.error?.message || JSON.stringify(json);
    } catch {
      detail = await res.text().catch(() => "");
    }
    throw new Error(`Graph sendMail failed (${res.status}): ${detail}`);
  }
}
